import React, { useState, useRef, useEffect } from 'react'
import { ChevronDown } from 'lucide-react'
import { Input } from './input'
import { Label } from './label'
import { Button } from './button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './popover'
import { cn } from '@/lib/utils'

// Country data with flags and dial codes
const COUNTRIES = [
  { code: 'SA', name: 'Saudi Arabia', dialCode: '+966', flag: '🇸🇦' },
  { code: 'AE', name: 'United Arab Emirates', dialCode: '+971', flag: '🇦🇪' },
  { code: 'KW', name: 'Kuwait', dialCode: '+965', flag: '🇰🇼' },
  { code: 'QA', name: 'Qatar', dialCode: '+974', flag: '🇶🇦' },
  { code: 'BH', name: 'Bahrain', dialCode: '+973', flag: '🇧🇭' },
  { code: 'OM', name: 'Oman', dialCode: '+968', flag: '🇴🇲' },
  { code: 'EG', name: 'Egypt', dialCode: '+20', flag: '🇪🇬' },
  { code: 'JO', name: 'Jordan', dialCode: '+962', flag: '🇯🇴' },
  { code: 'LB', name: 'Lebanon', dialCode: '+961', flag: '🇱🇧' },
  { code: 'IQ', name: 'Iraq', dialCode: '+964', flag: '🇮🇶' },
  { code: 'US', name: 'United States', dialCode: '+1', flag: '🇺🇸' },
  { code: 'GB', name: 'United Kingdom', dialCode: '+44', flag: '🇬🇧' },
  { code: 'IN', name: 'India', dialCode: '+91', flag: '🇮🇳' },
  { code: 'PK', name: 'Pakistan', dialCode: '+92', flag: '🇵🇰' },
  { code: 'BD', name: 'Bangladesh', dialCode: '+880', flag: '🇧🇩' },
  { code: 'PH', name: 'Philippines', dialCode: '+63', flag: '🇵🇭' },
  { code: 'ID', name: 'Indonesia', dialCode: '+62', flag: '🇮🇩' },
  { code: 'TR', name: 'Turkey', dialCode: '+90', flag: '🇹🇷' },
  { code: 'FR', name: 'France', dialCode: '+33', flag: '🇫🇷' },
  { code: 'DE', name: 'Germany', dialCode: '+49', flag: '🇩🇪' },
  { code: 'IT', name: 'Italy', dialCode: '+39', flag: '🇮🇹' },
  { code: 'ES', name: 'Spain', dialCode: '+34', flag: '🇪🇸' },
  { code: 'CN', name: 'China', dialCode: '+86', flag: '🇨🇳' },
  { code: 'JP', name: 'Japan', dialCode: '+81', flag: '🇯🇵' },
  { code: 'KR', name: 'South Korea', dialCode: '+82', flag: '🇰🇷' },
  { code: 'AU', name: 'Australia', dialCode: '+61', flag: '🇦🇺' },
  { code: 'CA', name: 'Canada', dialCode: '+1', flag: '🇨🇦' },
  { code: 'BR', name: 'Brazil', dialCode: '+55', flag: '🇧🇷' },
  { code: 'MX', name: 'Mexico', dialCode: '+52', flag: '🇲🇽' },
  { code: 'RU', name: 'Russia', dialCode: '+7', flag: '🇷🇺' },
]

interface PhoneInputProps {
  value?: string
  onChange?: (value: string) => void
  onBlur?: () => void
  placeholder?: string
  className?: string
  id?: string
  label?: React.ReactNode
  error?: boolean
  errorMessage?: string
  disabled?: boolean
}

export const PhoneInput: React.FC<PhoneInputProps> = ({
  value = '',
  onChange,
  onBlur,
  placeholder = 'Enter phone number',
  className,
  id,
  label,
  error,
  errorMessage,
  disabled,
}) => {
  const [selectedCountry, setSelectedCountry] = useState(COUNTRIES[0]) // Default to Saudi Arabia
  const [isOpen, setIsOpen] = useState(false)
  const [phoneNumber, setPhoneNumber] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)
  const isUpdatingFromProps = useRef(false)
  const prevValueRef = useRef<string | undefined>(undefined)
  const prevComputedValueRef = useRef<string>('')
  const isInitialMount = useRef(true)
  const onChangeRef = useRef(onChange)
  
  // Keep onChange ref updated
  useEffect(() => {
    onChangeRef.current = onChange
  }, [onChange])

  // Parse existing value to extract country code and phone number
  useEffect(() => {
    const currentValue = value || ''
    const prevValue = prevValueRef.current
    
    // Always process on first mount, or if value has changed
    if (prevValue !== currentValue || (isInitialMount.current && currentValue)) {
      isUpdatingFromProps.current = true
      prevValueRef.current = currentValue
      
      if (currentValue) {
        // Check if value starts with a country code
        const country = COUNTRIES.find((c) => currentValue.startsWith(c.dialCode))
        if (country) {
          const numberWithoutCode = currentValue.replace(country.dialCode, '').trim()
          setSelectedCountry(country)
          setPhoneNumber(numberWithoutCode)
        } else {
          // If no country code found, check if it's a local Saudi number (starts with 5 or 9)
          if (currentValue.match(/^[59]/)) {
            setSelectedCountry(COUNTRIES[0]) // Saudi Arabia
            setPhoneNumber(currentValue)
          } else {
            setPhoneNumber(currentValue)
          }
        }
      } else {
        // Only clear if we had a previous value
        if (prevValue) {
          setPhoneNumber('')
          setSelectedCountry(COUNTRIES[0]) // Reset to default
        }
      }
      
      if (isInitialMount.current) {
        isInitialMount.current = false
      }
      
      // Reset flag after state updates
      setTimeout(() => {
        isUpdatingFromProps.current = false
      }, 0)
    }
  }, [value])

  // Update parent when phone number or country changes (only on user interaction)
  useEffect(() => {
    // Skip on initial mount and when updating from props
    if (isInitialMount.current) {
      isInitialMount.current = false
      // Update the ref with initial computed value
      const initialValue = phoneNumber
        ? `${selectedCountry.dialCode}${phoneNumber}`
        : ''
      prevComputedValueRef.current = initialValue
      return
    }
    
    if (isUpdatingFromProps.current) return
    
    if (onChangeRef.current) {
      const fullNumber = phoneNumber
        ? `${selectedCountry.dialCode}${phoneNumber}`
        : ''
      // Only call onChange if the computed value is different from previous computed value
      // This prevents unnecessary updates and potential loops
      if (fullNumber !== prevComputedValueRef.current) {
        prevComputedValueRef.current = fullNumber
        onChangeRef.current(fullNumber)
      }
    }
  }, [phoneNumber, selectedCountry])

  const handleCountrySelect = (country: typeof COUNTRIES[0]) => {
    setSelectedCountry(country)
    setIsOpen(false)
    inputRef.current?.focus()
  }

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value.replace(/\D/g, '') // Only numbers
    setPhoneNumber(input)
  }

  return (
    <div className="space-y-2">
      {label && <Label htmlFor={id}>{label}</Label>}
      <div className="flex">
        <Popover open={isOpen} onOpenChange={setIsOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              className={cn(
                'rounded-r-none border-r-0 px-3 h-9',
                error && 'border-destructive',
                disabled && 'opacity-50 cursor-not-allowed'
              )}
              disabled={disabled}
            >
              <span className="text-lg mr-2">{selectedCountry.flag}</span>
              <span className="text-sm">{selectedCountry.dialCode}</span>
              <ChevronDown className="ml-2 h-4 w-4 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80 p-0" align="start">
            <div className="max-h-[300px] overflow-y-auto">
              <div className="p-2 sticky top-0 bg-background z-10 border-b">
                <Input
                  placeholder="Search country..."
                  className="mb-2"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <div className="divide-y">
                {COUNTRIES.filter((country) => {
                  if (!searchQuery) return true
                  const query = searchQuery.toLowerCase()
                  return (
                    country.name.toLowerCase().includes(query) ||
                    country.dialCode.includes(query) ||
                    country.code.toLowerCase().includes(query)
                  )
                }).map((country) => (
                  <button
                    key={country.code}
                    type="button"
                    className="w-full px-4 py-2 text-left hover:bg-accent flex items-center gap-3"
                    onClick={() => {
                      handleCountrySelect(country)
                      setSearchQuery('')
                    }}
                  >
                    <span className="text-lg">{country.flag}</span>
                    <span className="flex-1">{country.name}</span>
                    <span className="text-sm text-muted-foreground">
                      {country.dialCode}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </PopoverContent>
        </Popover>
        <Input
          ref={inputRef}
          id={id}
          type="tel"
          value={phoneNumber}
          onChange={handlePhoneChange}
          onBlur={onBlur}
          placeholder={placeholder}
          className={cn(
            'rounded-l-none flex-1',
            error && 'border-destructive',
            className
          )}
          disabled={disabled}
        />
      </div>
      {error && errorMessage && (
        <p className="text-sm text-destructive mt-1">{errorMessage}</p>
      )}
    </div>
  )
}

